import React, { useState, useEffect } from 'react';
import { workAssignmentAPI, branchAPI, holidayAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiEye, FiCalendar, FiUsers } from '../components/Icons';
import styled from 'styled-components';

const PageContainer = styled.div`
  width: calc(100vw - 280px);
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  max-width: none;
  overflow-x: hidden;
  
  @media (max-width: 1024px) {
    width: calc(100vw - 260px);
  }
  
  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  box-sizing: border-box;
  margin: 0;
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xs};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border.light};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.lg};
    padding: ${props => props.theme.spacing.md};
    
    h2 {
      margin: 0;
      font-size: 1.5rem;
      text-align: center;
    }
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  
  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: ${props => props.theme.spacing.lg};
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: ${props => props.theme.spacing.md};
    }
  }
`;

const Button = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.theme.shadows.light};
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    font-size: 12px;
  }
`;

const BranchesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin-left: 0;
  margin-right: 0;
  
  @media (max-width: 1400px) {
    gap: ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(5, 1fr);
    gap: ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${props => props.theme.spacing.xs};
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.xs};
  }
  
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarDay = styled.div`
  background: ${props => props.theme.colors.surface};
  min-height: 180px;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  position: relative;
  transition: all 0.3s ease;
  
  &.today {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  @media (max-width: 768px) {
    min-height: 160px;
    padding: ${props => props.theme.spacing.sm};
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 600;
  font-size: 18px;
  color: ${props => props.theme.colors.text.primary};
  
  .date-number {
    font-size: 24px;
    color: ${props => props.theme.colors.primary};
    font-weight: 700;
  }
  
  .day-name {
    font-size: 15px;
    color: ${props => props.theme.colors.text.secondary};
    text-transform: uppercase;
    font-weight: 600;
  }
`;

const HolidayChip = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.6;
  text-align: center;
  background: #930c0014;
  color: #930c00;
  border: 1px solid #930c0033;
`;

const RoleSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: ${props => props.theme.spacing.xs};
  
  .branch-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px;
    border-radius: ${props => props.theme.borderRadius.small};
    border: 1px solid transparent;
    transition: all 0.3s ease;
    
    /* Per-branch staffing status color coding (calm tints, match Branch/Multi-Branch) */
    &[data-staffing-status="adequate"] {
      background: #eef5f0;
      border-color: #cfe3d7;
    }

    &[data-staffing-status="insufficient"] {
      background: #fdf3f1;
      border-color: #e7c9c3;
    }
    
    .branch-name {
      font-size: 14px;
      font-weight: 700;
      color: ${props => props.theme.colors.text.primary};
      text-align: center;
      margin-bottom: 3px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .shifts-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .shift-row {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        
        .shift-label {
          font-size: 12px;
          font-weight: 700;
          color: ${props => props.theme.colors.text.secondary};
          min-width: 12px;
        }
        
        .role-dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          
          &.manager { background: #e91e63; }
          &.makeup { background: #9c27b0; }
          &.photographer { background: #2196f3; }
          &.retoucher { background: #4caf50; }
          
          &.zero {
            background: #f5f5f5;
            color: #999;
            border: 1px solid #e7e3db;
          }
        }
      }
    }
  }
`;

const StaffingRequirements = styled.div`
  background: #fbf4e6;
  border: 1px solid #e3c07a;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.md} 0;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.light};

  .requirements-title {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.xs};
  }

  .requirements-text {
    font-size: 0.875rem;
    color: ${props => props.theme.colors.text.secondary};
    
    .legend {
      display: flex;
      justify-content: center;
      gap: ${props => props.theme.spacing.lg};
      margin-top: ${props => props.theme.spacing.sm};
      flex-wrap: wrap;
      
      .legend-item {
        display: flex;
        align-items: center;
        gap: ${props => props.theme.spacing.xs};
        font-size: 0.75rem;
        
        .color-box {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          border: 1px solid #e7e3db;
        }
      }
    }
  }
`;

const RoleColorsLegend = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.md} 0;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.card};

  .legend-title {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.sm};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.xs};
  }

  .role-legend {
    display: flex;
    justify-content: center;
    gap: ${props => props.theme.spacing.lg};
    flex-wrap: wrap;

    .role-item {
      display: flex;
      align-items: center;
      gap: ${props => props.theme.spacing.xs};
      font-size: 0.875rem;
      color: ${props => props.theme.colors.text.secondary};
      font-weight: 500;
      
      .role-dot {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 10px;
        font-weight: 700;
        
        &.manager { background: #e91e63; }
        &.makeup { background: #9c27b0; }
        &.photographer { background: #2196f3; }
        &.retoucher { background: #4caf50; }
      }
    }
  }
`;

const BirdEyeViewPage = () => {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [branches, setBranches] = useState([]);
  const [branchAssignments, setBranchAssignments] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const { accessibleBranches } = useAuth();

  // Helper function to create timezone-safe date string
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // Refetch when the displayed YEAR changes (Prev/Next across December),
  // otherwise navigating into a new year kept showing last year's holidays.
  const currentYear = currentDate.getFullYear();
  useEffect(() => {
    fetchHolidays(currentYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear]);

  useEffect(() => {
    if (branches.length > 0) {
      fetchAllBranchAssignments();
    }
  }, [branches, currentDate]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll();
      // For admins and managers, show all branches
      if (accessibleBranches === 'ALL_BRANCHES') {
        setBranches(response.data);
      } else {
        // Filter to only accessible branches for other roles
        const filteredBranches = response.data.filter(branch => 
          accessibleBranches.includes(branch.id)
        );
        setBranches(filteredBranches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchHolidays = async (year) => {
    try {
      const response = await holidayAPI.getAll({ year });
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setHolidays([]);
    }
  };

  const fetchAllBranchAssignments = async () => {
    setLoading(true);
    try {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const assignmentPromises = branches.map(async (branch) => {
        try {
          const response = await workAssignmentAPI.getBranchCalendar(
            branch.id,
            getLocalDateString(startDate),
            getLocalDateString(endDate)
          );
          
          if (response.data && response.data.length > 0) {
            const allAssignments = [];
            response.data[0].schedules.forEach(schedule => {
              schedule.day_shift_assignments.forEach(assignment => {
                allAssignments.push({...assignment, shift_type: 'day'});
              });
              schedule.noon_shift_assignments.forEach(assignment => {
                allAssignments.push({...assignment, shift_type: 'noon'});
              });
            });
            return { branchId: branch.id, assignments: allAssignments };
          }
          return { branchId: branch.id, assignments: [] };
        } catch (error) {
          console.error(`Error fetching assignments for branch ${branch.id}:`, error);
          return { branchId: branch.id, assignments: [] };
        }
      });

      const results = await Promise.all(assignmentPromises);
      const assignmentsMap = {};
      results.forEach(result => {
        assignmentsMap[result.branchId] = result.assignments;
      });
      
      setBranchAssignments(assignmentsMap);
    } catch (error) {
      console.error('Error fetching branch assignments:', error);
      setBranchAssignments({});
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    for (let date = 1; date <= lastDay.getDate(); date++) {
      days.push(new Date(year, month, date));
    }
    
    return days;
  };

  const getAssignmentsForDate = (branchId, date) => {
    const dateStr = getLocalDateString(date);
    const assignments = branchAssignments[branchId] || [];
    return assignments.filter(assignment => assignment.assignment_date === dateStr);
  };

  // One chip per holiday name; if only one holiday group has it, tag the group.
  const getHolidayChipsForDate = (date) => {
    const dateStr = getLocalDateString(date);
    const todays = holidays.filter(holiday => holiday.date === dateStr);
    const byName = new Map();
    todays.forEach(h => {
      const group = h.holiday_group?.name;
      if (!byName.has(h.name)) byName.set(h.name, new Set());
      if (group) byName.get(h.name).add(group);
    });
    return Array.from(byName.entries()).map(([name, groups]) => {
      const groupList = Array.from(groups).sort();
      return groupList.length === 1 ? `${name} (${groupList[0]})` : name;
    });
  };

  const getRoleCountsByShift = (assignments) => {
    const dayShiftCounts = { manager: 0, makeup: 0, photographer: 0, retoucher: 0 };
    const noonShiftCounts = { manager: 0, makeup: 0, photographer: 0, retoucher: 0 };
    
    // Map actual roles to display categories
    const roleToCategory = {
      'store_manager': 'manager',
      'manager_assistant': 'manager',
      'makeup': 'makeup',
      'makeup_supervisor': 'makeup',
      'photographer': 'photographer',
      'photographer_supervisor': 'photographer',
      'retoucher': 'retoucher',
      'retoucher_supervisor': 'retoucher'
    };
    
    assignments.forEach(assignment => {
      const role = assignment.role;
      const shift = assignment.shift_type;
      const category = roleToCategory[role];
      
      if (category) {
        if (shift === 'day') {
          dayShiftCounts[category]++;
        } else if (shift === 'noon') {
          noonShiftCounts[category]++;
        }
      }
    });
    
    return { dayShiftCounts, noonShiftCounts };
  };

  const checkStaffingRequirements = (assignments) => {
    const { dayShiftCounts, noonShiftCounts } = getRoleCountsByShift(assignments);
    
    const dayShiftAdequate = dayShiftCounts.makeup >= 1 && dayShiftCounts.photographer >= 1;
    const noonShiftAdequate = noonShiftCounts.makeup >= 1 && noonShiftCounts.photographer >= 1;
    
    if (dayShiftAdequate && noonShiftAdequate) {
      return 'adequate';
    } else {
      return 'insufficient';
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prevDate => {
      const newMonth = prevDate.getMonth() + direction;
      return new Date(prevDate.getFullYear(), newMonth, 1);
    });
  };

  const calendarDays = getCalendarDays();
  const today = new Date();

  return (
    <PageContainer>
      <ContentWrapper>
        <PageHeader>
          <HeaderContent>
            <div className="header-top">
              <h2><FiEye style={{ marginRight: '8px' }} />Bird's Eye View</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Button onClick={() => navigateMonth(-1)}>« Prev</Button>
                <span style={{ 
                  margin: '0 10px', 
                  fontWeight: 'bold',
                  fontSize: 'clamp(14px, 2.5vw, 18px)',
                  textAlign: 'center',
                  minWidth: '150px'
                }}>
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <Button onClick={() => navigateMonth(1)}>Next »</Button>
              </div>
            </div>
          </HeaderContent>
        </PageHeader>

        {/* Staffing Requirements Banner */}
        <StaffingRequirements>
          <div className="requirements-title">
            <FiCalendar /> Overview: Shift Staffing Requirements
          </div>
          <div className="requirements-text">
            Each shift (Day & Noon) must have at least <strong>1 Makeup Artist</strong> and <strong>1 Photographer</strong>
            <div className="legend">
              <div className="legend-item">
                <div className="color-box" style={{ backgroundColor: '#fdf3f1', border: '2px solid #e7c9c3' }}></div>
                Branch Insufficient
              </div>
              <div className="legend-item">
                <div className="color-box" style={{ backgroundColor: '#eef5f0', border: '2px solid #cfe3d7' }}></div>
                Branch Adequate
              </div>
              <div className="legend-item">
                <HolidayChip as="span" style={{ display: 'inline-block' }}>Public holiday</HolidayChip>
              </div>
            </div>
          </div>
        </StaffingRequirements>

        {/* Role Colors Legend */}
        <RoleColorsLegend>
          <div className="legend-title">
            <FiUsers /> Role Color Guide
          </div>
          <div className="role-legend">
            <div className="role-item">
              <div className="role-dot manager">M</div>
              Manager
            </div>
            <div className="role-item">
              <div className="role-dot makeup">MU</div>
              Makeup Artist
            </div>
            <div className="role-item">
              <div className="role-dot photographer">P</div>
              Photographer
            </div>
            <div className="role-item">
              <div className="role-dot retoucher">R</div>
              Retoucher
            </div>
          </div>
        </RoleColorsLegend>

        {loading && <div>Loading overview...</div>}

        <BranchesGrid>
          {calendarDays.map((date, index) => {
            const isToday = date.toDateString() === today.toDateString();
            
            // Get data for all branches on this date
            const branchesForDate = branches.map(branch => ({
              branch,
              assignments: getAssignmentsForDate(branch.id, date)
            }));
            
            return (
              <CalendarDay
                key={index}
                className={isToday ? 'today' : ''}
              >
                <DayHeader>
                  <span className="date-number">{date.getDate()}</span>
                  <span className="day-name">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                </DayHeader>

                {getHolidayChipsForDate(date).map((label) => (
                  <HolidayChip key={label} title={label} style={{ marginBottom: '4px' }}>
                    {label}
                  </HolidayChip>
                ))}

                <RoleSummary>
                  {branchesForDate.map(({ branch, assignments }) => {
                    const { dayShiftCounts, noonShiftCounts } = getRoleCountsByShift(assignments);
                    const branchStaffingStatus = checkStaffingRequirements(assignments);
                    
                    return (
                      <div key={branch.id} className="branch-row" data-staffing-status={branchStaffingStatus}>
                        <div className="branch-name" title={branch.name}>
                          {branch.name}
                        </div>
                        <div className="shifts-container">
                          {/* Day Shift Row */}
                          <div className="shift-row">
                            <span className="shift-label">D</span>
                            <div className={`role-dot manager ${dayShiftCounts.manager === 0 ? 'zero' : ''}`} title={`${branch.name} - Managers - Day`}>
                              {dayShiftCounts.manager || '0'}
                            </div>
                            <div className={`role-dot makeup ${dayShiftCounts.makeup === 0 ? 'zero' : ''}`} title={`${branch.name} - Makeup - Day`}>
                              {dayShiftCounts.makeup || '0'}
                            </div>
                            <div className={`role-dot photographer ${dayShiftCounts.photographer === 0 ? 'zero' : ''}`} title={`${branch.name} - Photographers - Day`}>
                              {dayShiftCounts.photographer || '0'}
                            </div>
                            <div className={`role-dot retoucher ${dayShiftCounts.retoucher === 0 ? 'zero' : ''}`} title={`${branch.name} - Retouchers - Day`}>
                              {dayShiftCounts.retoucher || '0'}
                            </div>
                          </div>
                          
                          {/* Noon Shift Row */}
                          <div className="shift-row">
                            <span className="shift-label">N</span>
                            <div className={`role-dot manager ${noonShiftCounts.manager === 0 ? 'zero' : ''}`} title={`${branch.name} - Managers - Noon`}>
                              {noonShiftCounts.manager || '0'}
                            </div>
                            <div className={`role-dot makeup ${noonShiftCounts.makeup === 0 ? 'zero' : ''}`} title={`${branch.name} - Makeup - Noon`}>
                              {noonShiftCounts.makeup || '0'}
                            </div>
                            <div className={`role-dot photographer ${noonShiftCounts.photographer === 0 ? 'zero' : ''}`} title={`${branch.name} - Photographers - Noon`}>
                              {noonShiftCounts.photographer || '0'}
                            </div>
                            <div className={`role-dot retoucher ${noonShiftCounts.retoucher === 0 ? 'zero' : ''}`} title={`${branch.name} - Retouchers - Noon`}>
                              {noonShiftCounts.retoucher || '0'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </RoleSummary>
              </CalendarDay>
            );
          })}
        </BranchesGrid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default BirdEyeViewPage;